<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 1200px;">

      <!-- Header -->
      <div class="table-header-filters pa-4 mb-6">
        <v-row dense align="center">
          <v-col cols="12" sm="auto">
            <div class="period-pills">
              <button
                v-for="opt in periodOptions"
                :key="opt.value"
                class="period-pill"
                :class="{ active: period === opt.value }"
                @click="period = opt.value"
              >
                <v-icon x-small class="mr-1">{{ opt.icon }}</v-icon>
                {{ opt.label }}
              </button>
            </div>
          </v-col>

          <!-- Custom date range -->
          <v-col v-if="period === 'custom'" cols="12" sm="auto" class="d-flex align-center" style="gap: 6px;">
            <v-menu v-model="menuFrom" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
                  {{ formatDate(dateFrom) }}
                </button>
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable @input="menuFrom = false; fetchData()" />
            </v-menu>
            <span class="date-pill-sep">—</span>
            <v-menu v-model="menuTo" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-end</v-icon>
                  {{ formatDate(dateTo) }}
                </button>
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable @input="menuTo = false; fetchData()" />
            </v-menu>
          </v-col>

          <v-col cols="auto" class="d-flex align-center ml-auto" style="gap: 4px;">
            <v-switch
              v-model="showNightHours"
              dense hide-details
              color="blue-grey"
              class="mt-0 pt-0 mr-1"
              label="00–06"
            />
            <v-switch
              v-model="showRevenue"
              dense hide-details
              color="cyan darken-1"
              class="mt-0 pt-0 mr-2"
              :label="showRevenue ? 'Promet' : 'Br. računa'"
            />
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="{ name: `${targetModifier}.all-reports.reportMenu` }" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="cyan darken-1" size="48" />
        <div class="mt-4 grey--text">Učitavanje podataka...</div>
      </div>

      <template v-else>

        <!-- Chart -->
        <v-card outlined rounded="lg" class="pa-6 mb-6">
          <div class="section-title mb-4">Heatmap — {{ periodLabel }}</div>
          <apexchart
            type="bar"
            height="300"
            :options="chartOptions"
            :series="chartSeries"
          />
        </v-card>

        <!-- Table -->
        <v-card outlined rounded="lg" class="pa-6">
          <div class="section-title mb-4">Tablica po satima</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Sat</th>
                <th class="text-right">Promet</th>
                <th class="text-right">Br. računa</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in tableRows" :key="i" :class="{ 'active-hour': row.isActiveHour }">
                <td>
                  <span class="hour-label">{{ row.label }}</span>
                  <span v-if="row.isActiveHour" class="ml-2 caption cyan--text text--darken-1">← sada</span>
                </td>
                <td class="text-right">
                  <span :style="{ fontWeight: row.totalEur > 0 ? '600' : 'normal', color: row.totalEur > 0 ? '#00838F' : '#9CA3AF' }">
                    {{ row.totalEur > 0 ? formatEur(row.totalEur) : '—' }}
                  </span>
                </td>
                <td class="text-right">
                  <span :style="{ color: row.count > 0 ? 'inherit' : '#9CA3AF' }">
                    {{ row.count > 0 ? row.count : '—' }}
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr style="background: #F8FAFC; border-top: 2px solid #E0E6ED;">
                <td><strong>Ukupno</strong></td>
                <td class="text-right"><strong>{{ formatEur(totalEur) }}</strong></td>
                <td class="text-right"><strong>{{ totalCount }}</strong></td>
              </tr>
            </tfoot>
          </v-simple-table>
        </v-card>

      </template>
    </div>
  </v-container>
</template>

<script>
import { getFirestore, collection, query, where, getAggregateFromServer, sum, count } from 'firebase/firestore'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import VueApexCharts from 'vue-apexcharts'
import Vue from 'vue'

Vue.use(VueApexCharts)
Vue.component('apexchart', VueApexCharts)

const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`)
const today = new Date().toISOString().substr(0, 10)

function heatColor (ratio) {
  // green(76,175,80) → yellow(255,235,59) → red(244,67,54)
  let r, g, b
  if (ratio < 0.5) {
    const t = ratio * 2
    r = Math.round(76 + (255 - 76) * t)
    g = Math.round(175 + (235 - 175) * t)
    b = Math.round(80 + (59 - 80) * t)
  } else {
    const t = (ratio - 0.5) * 2
    r = Math.round(255 + (244 - 255) * t)
    g = Math.round(235 + (67 - 235) * t)
    b = Math.round(59 + (54 - 59) * t)
  }
  return `rgb(${r},${g},${b})`
}

export default {
  name: 'HeatmapProhoda',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    return {
      margin: '0px',
      targetModifier: '',
      loading: false,
      period: 'today',
      showRevenue: true,
      showNightHours: false,
      menuFrom: false,
      menuTo: false,
      dateFrom: today,
      dateTo: today,
      periodOptions: [
        { value: 'today', label: 'Danas', icon: 'mdi-calendar-today' },
        { value: 'yesterday', label: 'Jučer', icon: 'mdi-calendar-arrow-left' },
        { value: 'week', label: 'Ovaj tjedan', icon: 'mdi-calendar-week' },
        { value: 'custom', label: 'Prilagođeno', icon: 'mdi-calendar-edit' }
      ],
      hourData: Array.from({ length: 24 }, () => ({ totalCents: 0, count: 0 }))
    }
  },

  computed: {
    periodLabel () {
      const m = this.periodOptions.find(o => o.value === this.period)
      if (this.period === 'custom') return `${this.formatDate(this.dateFrom)} — ${this.formatDate(this.dateTo)}`
      return m ? m.label : ''
    },

    periodDays () {
      const now = new Date()
      const todaySec = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000)
      if (this.period === 'today') return [todaySec]
      if (this.period === 'yesterday') return [todaySec - 86400]
      if (this.period === 'custom') {
        const fromSec = Math.floor(new Date(this.dateFrom).getTime() / 1000)
        const toSec = Math.floor(new Date(this.dateTo).getTime() / 1000)
        const days = []
        for (let d = fromSec; d <= toSec; d += 86400) days.push(d)
        return days
      }
      // week
      const day = now.getDay() || 7
      const mondaySec = todaySec - (day - 1) * 86400
      const days = []
      for (let d = mondaySec; d <= todaySec; d += 86400) days.push(d)
      return days
    },

    currentHour () {
      return this.period === 'today' ? new Date().getHours() : -1
    },

    visibleIndices () {
      return Array.from({ length: 24 }, (_, i) => i)
        .filter(i => this.showNightHours || i >= 6)
    },

    visibleHourData () {
      return this.visibleIndices.map(i => this.hourData[i])
    },

    barColors () {
      const values = this.visibleHourData.map(h => this.showRevenue ? h.totalCents : h.count)
      const maxVal = Math.max(...values, 1)
      return values.map(v => heatColor(v / maxVal))
    },

    chartSeries () {
      const data = this.visibleHourData.map(h =>
        this.showRevenue ? parseFloat((h.totalCents / 100).toFixed(2)) : h.count
      )
      return [{ name: this.showRevenue ? 'Promet (EUR)' : 'Broj računa', data }]
    },

    chartOptions () {
      const isRevenue = this.showRevenue
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Raleway, sans-serif' },
        plotOptions: { bar: { horizontal: false, columnWidth: '80%', borderRadius: 3, distributed: true } },
        colors: this.barColors,
        xaxis: {
          categories: this.visibleIndices.map(i => HOUR_LABELS[i]),
          labels: { style: { fontSize: '10px' }, rotate: -45, rotateAlways: true }
        },
        yaxis: {
          labels: {
            formatter: isRevenue ? v => `€${v.toFixed(0)}` : v => String(Math.round(v))
          }
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: {
          y: {
            formatter: isRevenue
              ? v => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
              : v => `${v} računa`
          }
        },
        grid: { borderColor: '#E0E6ED', strokeDashArray: 3 }
      }
    },

    tableRows () {
      return this.visibleIndices.map(i => ({
        label: HOUR_LABELS[i],
        totalEur: this.hourData[i].totalCents / 100,
        count: this.hourData[i].count,
        isActiveHour: i === this.currentHour
      }))
    },

    totalEur () {
      return this.visibleHourData.reduce((acc, h) => acc + h.totalCents, 0) / 100
    },
    totalCount () {
      return this.visibleHourData.reduce((acc, h) => acc + h.count, 0)
    }
  },

  watch: {
    period (val) {
      if (val !== 'custom') this.fetchData()
    }
  },

  async created () {
    this.margin = state.getMargin()
    const sideNav = this.$route.meta?.sideNav
    if (sideNav === 'Company') this.targetModifier = 'company'
    else if (sideNav === 'PointOfSale') this.targetModifier = 'pointOfSale'
    else if (sideNav === 'CashRegister') this.targetModifier = 'cashRegister'
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    await this.fetchData()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    formatEur (v) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
    },

    formatDate (d) {
      if (!d) return ''
      const [y, m, day] = d.split('-')
      return `${day}.${m}.${y}`
    },

    async fetchData () {
      this.loading = true
      try {
        const companyId = state.getCurrentCompany().id
        const db = getFirestore()
        const col = collection(db, `receipts/${companyId}/receipts`)
        const days = this.periodDays

        const hourData = await Promise.all(
          Array.from({ length: 24 }, async (_, h) => {
            const snaps = await Promise.all(
              days.map(dayStart => {
                const start = dayStart + h * 3600
                const end = start + 3600
                return getAggregateFromServer(
                  query(col,
                    where('issue_date_time', '>=', start),
                    where('issue_date_time', '<', end)
                  ),
                  { totalCents: sum('total'), cnt: count() }
                )
              })
            )
            const totalCents = snaps.reduce((acc, s) => acc + (s.data().totalCents || 0), 0)
            const cnt = snaps.reduce((acc, s) => acc + (s.data().cnt || 0), 0)
            return { totalCents, count: cnt }
          })
        )

        this.hourData = hourData
      } catch (err) {
        console.error('HeatmapProhoda:', err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}
.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.period-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.period-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1.5px solid #E0E6ED;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  outline: none;
}
.period-pill:hover {
  border-color: #00BCD4;
  color: #00838F;
  background: rgba(0, 188, 212, 0.05);
}
.period-pill.active {
  border-color: #00BCD4;
  background: rgba(0, 188, 212, 0.12);
  color: #00838F;
  font-weight: 600;
}
.date-pill {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
}
.date-pill-sep {
  font-size: 13px;
  color: #9CA3AF;
  font-weight: 500;
}
.section-title { font-size: 16px; font-weight: 700; color: #2d3748; }
.hour-label { font-family: monospace; font-size: 13px; }
.active-hour { background: rgba(0, 188, 212, 0.06); }
</style>
